import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Select,
  Spinner,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { EditIcon, CheckIcon, CloseIcon, DeleteIcon, WarningIcon } from '@chakra-ui/icons';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { secureFetch } from '../data/secureFetch';

const DataEditor = () => {
  const { email } = useSelector((state) => state.user);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFieldValues, setEditFieldValues] = useState({});
  const toast = useToast();
  const navigate = useNavigate(); // To navigate to 404 if needed

  useEffect(() => {
    if (email !== 'malanchuksam@gmail.com' && email !== 'sammym771@gmail.com') {
      navigate('/404'); // Redirect to a 404 page
    }
  }, [email, navigate]);

  const extractArrayData = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    return null;
  };

  // Fetch table names on mount
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const response = await secureFetch(`/api/tables`);
        const data = extractArrayData(response);

        if (!data) {
          throw new Error(response?.error || 'Unexpected response format while fetching tables');
        }

        setTables(data);
      } catch (error) {
        setTables([]);
        toast({
          title: 'Error fetching tables',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchTables();
  }, [toast]);

  // Fetch data for the selected table
  const fetchTableData = async (tableName) => {
    setLoading(true);
    try {
      const response = await secureFetch(`/api/tables/${tableName}`);
      const data = extractArrayData(response);

      if (!data) {
        throw new Error(response?.error || 'Unexpected response format while fetching table data');
      }

      setTableData(data);
    } catch (error) {
      setTableData([]);
      toast({
        title: `Error fetching data for ${tableName}`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle table selection
  const handleTableSelect = (event) => {
    const tableName = event.target.value;
    setSelectedTable(tableName);
    setEditFieldValues({});
    setEditingRecord(null);
    if (tableName) {
      fetchTableData(tableName);
    }
  };

  // Handle initiating edit
  const handleEditClick = (record) => {
    setEditingRecord(record.id);
    setEditFieldValues(record);
  };

  // Handle field change
  const handleFieldChange = (field, value) => {
    setEditFieldValues((prev) => ({ ...prev, [field]: value }));
  };

  // Handle saving an edit
  const handleSaveEdit = async () => {
    if (!editingRecord || !selectedTable) return;
    setLoading(true);
    try {
      await secureFetch(`/api/tables/${selectedTable}/${editingRecord}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        data: editFieldValues,
      });
      toast({
        title: 'Record updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      fetchTableData(selectedTable); // Refresh data after editing
    } catch (error) {
      toast({
        title: 'Error updating record',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setEditingRecord(null);
    }
  };

  // Handle canceling an edit
  const handleCancelEdit = () => {
    setEditingRecord(null);
    setEditFieldValues({});
  };

  const campaignImage = `https://${window.location.host}/images/share-image.png`;

  return (
    <Box p={5}>
      <Helmet>
        <title>Admin Panel - PurelyGive</title>
        <meta name="description" content="Administrative access to manage users, campaigns, and platform settings on PurelyGive. For authorized personnel only." />
        <meta name="keywords" content="admin panel, administration, user management, platform settings, PurelyGive" />
        <meta property="og:title" content="Admin Dashboard - PurelyGive" />
        <meta property="og:description" content="Access the PurelyGive admin panel to manage platform settings and oversee user activities." />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/admin" />
      </Helmet>
      <Heading mb={6}>Admin Table View</Heading>
      <Select placeholder="Select a table" onChange={handleTableSelect} mb={4}>
        {tables.map((table) => (
          <option key={table} value={table}>
            {table}
          </option>
        ))}
      </Select>

      <Flex mb={4} gap={3}>
        <IconButton
          icon={<DeleteIcon />}
          colorScheme="red"
          variant="outline"
          isDisabled={!selectedTable}
          aria-label="Empty Table"
          onClick={async () => {
            if (window.confirm(`Are you sure you want to empty table "${selectedTable}"?`)) {
              try {
                await secureFetch(`/api/tables/${selectedTable}`, {
                  method: 'DELETE',
                });
                toast({
                  title: `Table ${selectedTable} emptied.`,
                  status: 'success',
                  duration: 5000,
                  isClosable: true,
                });
                fetchTableData(selectedTable);
              } catch (err) {
                toast({
                  title: 'Failed to empty table',
                  description: err.message,
                  status: 'error',
                  duration: 5000,
                  isClosable: true,
                });
              }
            }
          }}
        />

        <IconButton
          icon={<WarningIcon />}
          colorScheme="red"
          aria-label="Empty All Tables"
          onClick={async () => {
            if (window.confirm('Are you sure you want to empty ALL tables? This cannot be undone.')) {
              try {
                await secureFetch(`/api/tables`, {
                  method: 'DELETE',
                });
                toast({
                  title: 'All tables emptied.',
                  status: 'success',
                  duration: 5000,
                  isClosable: true,
                });
                if (selectedTable) fetchTableData(selectedTable);
              } catch (err) {
                toast({
                  title: 'Failed to empty all tables',
                  description: err.message,
                  status: 'error',
                  duration: 5000,
                  isClosable: true,
                });
              }
            }
          }}
        />
      </Flex>
      {loading ? (
        <Spinner />
      ) : (
        selectedTable && (
          <Box overflowX="auto">
            <Table variant="striped" colorScheme="gray">
              <Thead>
                <Tr>
                  {tableData.length > 0 &&
                    Object.keys(tableData[0]).map((key) => (
                      <Th key={key}>{key}</Th>
                    ))}
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {tableData.map((record) => (
                  <Tr key={record.id}>
                    {Object.keys(record).map((field) => (
                      <Td key={field}>
                        {editingRecord === record.id ? (
                          <Input
                            value={editFieldValues[field] || ''}
                            onChange={(e) =>
                              handleFieldChange(field, e.target.value)
                            }
                          />
                        ) : (
                          record[field]?.toString()
                        )}
                      </Td>
                    ))}
                    <Td>
                      {editingRecord === record.id ? (
                        <Flex gap={2}>
                          <IconButton
                            icon={<CheckIcon />}
                            aria-label="Save"
                            onClick={handleSaveEdit}
                            colorScheme="green"
                          />
                          <IconButton
                            icon={<CloseIcon />}
                            aria-label="Cancel"
                            onClick={handleCancelEdit}
                            colorScheme="red"
                          />
                        </Flex>
                      ) : (
                        <IconButton
                          icon={<EditIcon />}
                          aria-label="Edit"
                          onClick={() => handleEditClick(record)}
                        />
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )
      )}
    </Box>
  );
};

export default DataEditor;
